import {
    AlternativePaymentMethod,
    CardPaymentTableRecordData,
    PaymentKind,
    PaymentState,
    PaymentTableRecord,
    PaymentTableRecordPaymentStatus,
    PreauthorizationStatus,
    PaymentType,
    PaymentsGetTableRecordsResult,
} from '@mews-commander/data-access';
import { TextKey } from '@mews-commander/enums';
import { TagVariant } from '@optimus-web/core';

export const getTagVariantFromPaymentState: Record<PaymentTableRecordPaymentStatus, TagVariant> = {
    [PaymentTableRecordPaymentStatus.Charged]: 'lime',
    [PaymentTableRecordPaymentStatus.Canceled]: 'basicBold',
    [PaymentTableRecordPaymentStatus.Pending]: 'primary',
    [PaymentTableRecordPaymentStatus.Failed]: 'danger',
    [PaymentTableRecordPaymentStatus.Verifying]: 'primary',
    [PaymentTableRecordPaymentStatus.WithBank]: 'primary',
    [PaymentTableRecordPaymentStatus.Settled]: 'basicBold',
};

export const paymentStatusTextKeyMap: Record<PaymentTableRecordPaymentStatus, Mews.TextKey> = {
    [PaymentTableRecordPaymentStatus.Charged]: TextKey.PaymentStateCharged,
    [PaymentTableRecordPaymentStatus.Canceled]: TextKey.PaymentStateCanceled,
    [PaymentTableRecordPaymentStatus.Pending]: TextKey.PaymentStatePending,
    [PaymentTableRecordPaymentStatus.Failed]: TextKey.PaymentStateFailed,
    [PaymentTableRecordPaymentStatus.Verifying]: TextKey.PaymentStateVerifying,
    [PaymentTableRecordPaymentStatus.WithBank]: TextKey.PaymentStateWithBank,
    [PaymentTableRecordPaymentStatus.Settled]: TextKey.PaymentStateSettled,
};

export const getTagVariantFromPreauthorizationState: Record<PreauthorizationStatus, TagVariant> = {
    [PreauthorizationStatus.Charged]: 'lime',
    [PreauthorizationStatus.Canceled]: 'basicBold',
    [PreauthorizationStatus.Pending]: 'primary',
    [PreauthorizationStatus.Failed]: 'danger',
    [PreauthorizationStatus.Verifying]: 'primary',
    [PreauthorizationStatus.Preauthorized]: 'lime',
    [PreauthorizationStatus.Chargeable]: 'primary',
    [PreauthorizationStatus.Expired]: 'warning',
};

export const preauthorizationStatusTextKeyMap: Record<PreauthorizationStatus, Mews.TextKey> = {
    [PreauthorizationStatus.Charged]: TextKey.PaymentStateCharged,
    [PreauthorizationStatus.Canceled]: TextKey.PaymentStateCanceled,
    [PreauthorizationStatus.Pending]: TextKey.PaymentStatePending,
    [PreauthorizationStatus.Failed]: TextKey.PaymentStateFailed,
    [PreauthorizationStatus.Verifying]: TextKey.PaymentStateVerifying,
    [PreauthorizationStatus.Preauthorized]: TextKey.Preauthorized,
    [PreauthorizationStatus.Chargeable]: TextKey.Chargeable,
    [PreauthorizationStatus.Expired]: TextKey.Expired,
};

export const paymentKindTextKeyMap: Record<PaymentKind, Mews.TextKey> = {
    [PaymentKind.Payment]: TextKey.Payment,
    [PaymentKind.Chargeback]: TextKey.Chargeback,
    [PaymentKind.ChargebackReversal]: TextKey.ChargebackReversal,
    [PaymentKind.Refund]: TextKey.Refund,
};

export const prepareTableData = (data: PaymentsGetTableRecordsResult, isPaymentAttemptsTable: boolean) => {
    if (isPaymentAttemptsTable) return data;

    const refunds = data.filter(({ refundedPaymentId }) => refundedPaymentId);

    return data
        .filter(({ refundedPaymentId }) => !refundedPaymentId)
        .flatMap((payment) => [payment, ...refunds.filter((refund) => refund.refundedPaymentId === payment.id)]);
};

export const isCashPayment = (discriminator: PaymentType): discriminator is PaymentType.Cash =>
    discriminator === PaymentType.Cash;

export const isTerminalPayment = (value: PaymentTableRecord['data']['value']): value is CardPaymentTableRecordData =>
    (value as CardPaymentTableRecordData).paymentTerminal != null;

export const isFailedOrPendingStatus = (status: PaymentTableRecordPaymentStatus) =>
    status === PaymentTableRecordPaymentStatus.Failed || status === PaymentTableRecordPaymentStatus.Pending;

export const getPaymentsTableStates = (isPaymentAttemptsTable: boolean) =>
    isPaymentAttemptsTable
        ? [PaymentState.Canceled, PaymentState.Failed, PaymentState.Pending, PaymentState.Verifying]
        : [PaymentState.Charged];

export const isAlternativePaymentMethodSepa = (
    paymentMethod: AlternativePaymentMethod
): paymentMethod is AlternativePaymentMethod.SepaDirectDebit =>
    paymentMethod === AlternativePaymentMethod.SepaDirectDebit;

export const getPaymentTypeMainText = (
    payment: PaymentTableRecord,
    textKey: Mews.TextKey,
    refundTextKey: Mews.TextKey
) => {
    if ((payment.kind === PaymentKind.Payment && payment.amount.value < 0) || payment.kind === PaymentKind.Refund) {
        return refundTextKey;
    }
    if (payment.kind && payment.kind !== PaymentKind.Payment) {
        return paymentKindTextKeyMap[payment.kind];
    }
    return textKey;
};
